import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { useAuth } from './AuthContext';
import {
  SubscriptionPlan,
  BillingCycle,
  UserSubscription,
  PlanLimits,
  SUBSCRIPTION_PLANS,
  TRIAL_DURATION_DAYS,
  calculateTrialEndDate,
  isTrialExpired,
  getDaysRemaining,
  getPlanById,
  canConnectPlatform
} from '@/modules/ecom-intelligence/types/subscription';

interface ConnectionCount {
  category: string;
  count: number;
}

interface SubscriptionContextType {
  // Subscription state
  subscription: UserSubscription | null;
  loading: boolean;

  // Trial status
  isTrialActive: boolean;
  isTrialExpired: boolean;
  daysRemaining: number;

  // Subscription status
  isSubscribed: boolean;
  currentPlan: SubscriptionPlan;
  planLimits: PlanLimits | null;

  // Feature access
  hasAccess: boolean; // True if in trial or subscribed
  canAccessFeature: (feature: keyof PlanLimits) => boolean;
  canConnectMore: (category: string) => { allowed: boolean; limit: number; current: number };

  // Services status (stops when subscription ends)
  servicesActive: boolean;

  // Actions
  refreshSubscription: () => Promise<void>;
  checkDuplicateApiKey: (apiKey: string, platformId: string) => Promise<{ isDuplicate: boolean; message?: string }>;

  // Paywall
  showPaywall: boolean;
  setShowPaywall: (show: boolean) => void;
}

const defaultSubscription: UserSubscription = {
  plan: 'free',
  billingCycle: 'monthly',
  trialStartDate: new Date(),
  trialEndDate: calculateTrialEndDate(new Date()),
  isTrialActive: true,
  isSubscribed: false
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [connectionCounts, setConnectionCounts] = useState<ConnectionCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  // Fetch subscription data
  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const db = getFirebaseFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const data = userDoc.data();

        if (data.subscription) {
          const sub: UserSubscription = {
            plan: data.subscription.plan || 'free',
            billingCycle: data.subscription.billingCycle || 'monthly',
            trialStartDate: data.subscription.trialStartDate?.toDate() || new Date(),
            trialEndDate: data.subscription.trialEndDate?.toDate() || calculateTrialEndDate(new Date()),
            isTrialActive: data.subscription.isTrialActive ?? true,
            isSubscribed: data.subscription.isSubscribed ?? false,
            subscriptionStartDate: data.subscription.subscriptionStartDate?.toDate(),
            subscriptionEndDate: data.subscription.subscriptionEndDate?.toDate(),
            stripeCustomerId: data.subscription.stripeCustomerId,
            stripeSubscriptionId: data.subscription.stripeSubscriptionId
          };
          setSubscription(sub);
        } else {
          // First time user - create trial subscription
          const newSubscription: UserSubscription = {
            plan: 'free',
            billingCycle: 'monthly',
            trialStartDate: new Date(),
            trialEndDate: calculateTrialEndDate(new Date()),
            isTrialActive: true,
            isSubscribed: false
          };

          await setDoc(doc(db, 'users', user.uid), {
            subscription: {
              plan: newSubscription.plan,
              billingCycle: newSubscription.billingCycle,
              trialStartDate: newSubscription.trialStartDate,
              trialEndDate: newSubscription.trialEndDate,
              isTrialActive: newSubscription.isTrialActive,
              isSubscribed: newSubscription.isSubscribed
            }
          }, { merge: true });

          setSubscription(newSubscription);
        }
      } else {
        // Create new user with trial
        const newSubscription: UserSubscription = {
          plan: 'free',
          billingCycle: 'monthly',
          trialStartDate: new Date(),
          trialEndDate: calculateTrialEndDate(new Date()),
          isTrialActive: true,
          isSubscribed: false
        };

        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName,
          createdAt: new Date(),
          subscription: {
            plan: newSubscription.plan,
            billingCycle: newSubscription.billingCycle,
            trialStartDate: newSubscription.trialStartDate,
            trialEndDate: newSubscription.trialEndDate,
            isTrialActive: newSubscription.isTrialActive,
            isSubscribed: newSubscription.isSubscribed
          }
        });

        setSubscription(newSubscription);
      }

      // Fetch connection counts
      try {
        const connectionsRef = collection(db, 'users', user.uid, 'connections');
        const connectionsSnap = await getDocs(connectionsRef);

        const counts: Record<string, number> = {};
        connectionsSnap.forEach(doc => {
          const data = doc.data();
          const category = data.category || 'Other';
          counts[category] = (counts[category] || 0) + 1;
        });

        setConnectionCounts(
          Object.entries(counts).map(([category, count]) => ({ category, count }))
        );
      } catch {
        setConnectionCounts([]);
      }

    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(defaultSubscription);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Check for duplicate API key
  const checkDuplicateApiKey = async (apiKey: string, platformId: string): Promise<{ isDuplicate: boolean; message?: string }> => {
    if (!user || !apiKey) return { isDuplicate: false };

    try {
      const db = getFirebaseFirestore();

      // Query all users' connections for this API key
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);

      for (const userDoc of usersSnap.docs) {
        // Skip current user
        if (userDoc.id === user.uid) continue;

        const connectionsRef = collection(db, 'users', userDoc.id, 'connections');
        const connectionsSnap = await getDocs(connectionsRef);

        for (const connDoc of connectionsSnap.docs) {
          const connData = connDoc.data();

          // Check if API key matches (hash comparison in production)
          if (connData.credentials?.apiKey === apiKey ||
              connData.credentials?.sellerId === apiKey ||
              connData.credentials?.supplierId === apiKey) {
            return {
              isDuplicate: true,
              message: 'Mükerrer hesap tespit edildi! Bu API anahtarı başka bir hesapta kullanılmaktadır. Lütfen ana hesabınızı kontrol edin veya destek ile iletişime geçin.'
            };
          }
        }
      }

      return { isDuplicate: false };
    } catch (error) {
      console.error('Error checking duplicate API key:', error);
      return { isDuplicate: false };
    }
  };

  // Computed values
  const isTrialActiveComputed = subscription?.isTrialActive && !isTrialExpired(subscription.trialEndDate);
  const isTrialExpiredComputed = subscription ? isTrialExpired(subscription.trialEndDate) : false;
  const daysRemainingComputed = subscription ? getDaysRemaining(subscription.trialEndDate) : 0;
  const isSubscribedComputed = subscription?.isSubscribed ?? false;
  const currentPlan = subscription?.plan || 'free';
  const planDetails = getPlanById(currentPlan);
  const planLimits = planDetails?.limits || null;

  // Has access: either in active trial or subscribed
  const hasAccess = isTrialActiveComputed || isSubscribedComputed;

  // Services are active only when user has access
  const servicesActive = hasAccess;

  // Feature access check
  const canAccessFeature = (feature: keyof PlanLimits): boolean => {
    if (!hasAccess) return false;
    if (!planLimits) return true; // During trial, allow all

    const value = planLimits[feature];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return true;
  };

  // Connection limit check
  const canConnectMore = (category: string): { allowed: boolean; limit: number; current: number } => {
    if (!hasAccess) return { allowed: false, limit: 0, current: 0 };

    // During trial, allow unlimited
    if (isTrialActiveComputed && !isSubscribedComputed) {
      return { allowed: true, limit: -1, current: 0 };
    }

    if (!planLimits) return { allowed: true, limit: -1, current: 0 };

    return canConnectPlatform(connectionCounts, category, planLimits);
  };

  // Auto-show paywall when trial expires
  useEffect(() => {
    if (isTrialExpiredComputed && !isSubscribedComputed) {
      setShowPaywall(true);
    }
  }, [isTrialExpiredComputed, isSubscribedComputed]);

  const value: SubscriptionContextType = {
    subscription,
    loading,
    isTrialActive: isTrialActiveComputed ?? false,
    isTrialExpired: isTrialExpiredComputed,
    daysRemaining: daysRemainingComputed,
    isSubscribed: isSubscribedComputed,
    currentPlan,
    planLimits,
    hasAccess,
    canAccessFeature,
    canConnectMore,
    servicesActive,
    refreshSubscription: fetchSubscription,
    checkDuplicateApiKey,
    showPaywall,
    setShowPaywall
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
